import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useT } from "../i18n/hooks.ts";
import { DialogActions } from "./DialogActions.tsx";

/** Elements a keyboard user can land on inside the dialog — also used by Popover.tsx's own (lighter) focus handling. */
export const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Exit-animation window — matches the `--transition-slow` (0.18s) token
 * the enter animation for `.dialog-backdrop`/`.dialog-card` uses. Kept
 * as a JS literal (not read from a CSS custom property) so it can drive
 * a `setTimeout` — reading it via `getComputedStyle` on the root would
 * work in one direction but add a re-flow on every close for no gain.
 * If the token in base.css moves, update this to match.
 */
const DIALOG_EXIT_MS = 180;

/** WCAG 2.3.3 — same OS-level "reduce motion" branch the `.dialog-card` CSS reduced-motion @media block uses; the exit-delay is short-circuited to zero under it so the user still gets an instant-close, not a 180 ms unresponsive window. Guarded because `matchMedia` isn't available in every test/SSR context. */
function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Second-audit finding: `Dialog.tsx`'s enter animation landed in point
 * 3 but the close path stayed a hard React unmount, so a dialog that
 * settled in visibly on open then vanished mid-blink on the way out.
 * The wrapped close below runs the exit animation (via `.dialog-
 * exiting` on backdrop + card), waits `DIALOG_EXIT_MS`, then invokes
 * the real parent-provided `onClose` (which unmounts). Exposed via
 * this context so `DialogActions.tsx`'s own Cancel button routes
 * through the same fade instead of triggering the parent's raw
 * `onClose` and skipping the animation. `null` outside a Dialog — a
 * `DialogActions` used standalone (rare) still calls its own `onClose`
 * prop directly.
 */
const DialogCloseContext = createContext<(() => void) | null>(null);

/**
 * Generic centered modal — backdrop + card, Escape and a backdrop click
 * both close it. Simpler than evalhid-web's own Dialog.tsx (no dragging,
 * no nested-dialog registry) since this app never opens more than one
 * dialog at a time.
 *
 * Rendered via a portal into document.body — a dialog mounted in place
 * can get trapped inside an ancestor's own CSS stacking context (e.g. a
 * position:relative topbar) and paint below unrelated elements (Leaflet's
 * own panes included) despite a higher z-index of its own; portaling
 * sidesteps that regardless of the ancestor.
 *
 * Traps Tab/Shift+Tab inside the card while open and restores focus to
 * whatever triggered the dialog on close.
 */
export function Dialog({
  title,
  titleIcon,
  titleAction,
  onClose,
  children,
  wide,
  fitContent,
  isDirty,
}: {
  title: string;
  /** A small glyph rendered inside the `<h3>`, before the text — `aria-hidden` so it's never announced as part of the title's own accessible name, unlike `titleAction` which sits outside the heading because it's a real interactive control. */
  titleIcon?: ReactNode;
  /** Rendered next to the title, kept outside the `<h3>` so `aria-labelledby="dialog-title"` only announces the plain title text. */
  titleAction?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** A wider card for content needing more than one column's worth of room (e.g. OptionsDialog.tsx's multi-field form). */
  wide?: boolean;
  /** Opt-out (`wide` only) from the shared 85vh cap the class enforces
   *  by default: OptionsDialog.tsx and LongitudinalProfileDialog.tsx both
   *  fit their tallest state well under that cap, so keeping it left
   *  ~950px of dialog around only a handful of inputs / ~450px under a
   *  fixed-height chart. Card sizes to content with a `min-height` floor
   *  and a `max-height: 85vh` ceiling instead — see `.dialog-card-fit-
   *  content` in dialog.css for why other wide dialogs (ProjectData,
   *  Georref, …) keep the stable-height default. */
  fitContent?: boolean;
  /** When true, a backdrop click or Escape asks to confirm discarding changes instead of closing immediately — otherwise editing a curve/pattern mid-edit and clicking outside silently threw away everything typed. */
  isDirty?: boolean;
}) {
  const t = useT();
  const cardRef = useRef<HTMLDivElement>(null);
  // The nested "discard changes" confirmation's own card — a real second
  // dialog stacked on top of the main one (`confirmDiscard`), not just a
  // style change to the same card. The Tab-trap below and the mount-focus
  // effect both need to target WHICHEVER of the two is actually on top, or
  // Tab cycles through the now-inert main dialog underneath while the
  // confirmation is showing, and its own Discard/Keep-editing buttons are
  // unreachable by keyboard.
  const confirmCardRef = useRef<HTMLDivElement>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [exiting, setExiting] = useState(false);
  const exitTimerRef = useRef<number | null>(null);

  const closeWithExit = useCallback(() => {
    // Already mid-exit: a second Escape/backdrop-click during the fade
    // shouldn't queue a second timer or restart the animation. First one
    // wins, real unmount is still on its way.
    if (exiting) return;
    if (prefersReducedMotion()) {
      onClose();
      return;
    }
    setExiting(true);
    exitTimerRef.current = window.setTimeout(() => {
      exitTimerRef.current = null;
      onClose();
    }, DIALOG_EXIT_MS);
  }, [exiting, onClose]);

  useEffect(
    () => () => {
      if (exitTimerRef.current !== null) clearTimeout(exitTimerRef.current);
    },
    []
  );

  function requestClose() {
    if (isDirty) setConfirmDiscard(true);
    else closeWithExit();
  }

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (confirmDiscard) setConfirmDiscard(false);
        else requestClose();
        return;
      }
      if (e.key !== "Tab") return;
      const activeCard = confirmDiscard ? confirmCardRef.current : cardRef.current;
      const focusable = activeCard?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!focusable || focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, isDirty, confirmDiscard]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusable = cardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable?.[0] ?? cardRef.current)?.focus();
    return () => previouslyFocused?.focus();
  }, []);

  // Moves focus into the confirmation's own first control the instant it
  // opens, and restores it back to wherever the main dialog had it once the
  // confirmation closes again (Keep editing / Escape) — same pattern as the
  // main dialog's own mount-focus effect above, just scoped to this nested
  // dialog's own open/close instead of the outer one's mount/unmount.
  useEffect(() => {
    if (!confirmDiscard) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusable = confirmCardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable?.[0] ?? confirmCardRef.current)?.focus();
    return () => previouslyFocused?.focus();
  }, [confirmDiscard]);

  return createPortal(
    <DialogCloseContext.Provider value={closeWithExit}>
      <div
        className={["dialog-backdrop", exiting && "dialog-exiting"].filter(Boolean).join(" ")}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) requestClose();
        }}
      >
        <div
          ref={cardRef}
          className={[
            "dialog-card",
            wide && "dialog-card-wide",
            wide && fitContent && "dialog-card-fit-content",
            exiting && "dialog-exiting",
          ]
            .filter(Boolean)
            .join(" ")}
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog-title"
          tabIndex={-1}
        >
          <div className="dialog-title-row">
            <h3 id="dialog-title" className="dialog-title">
              {titleIcon && (
                <span className="dialog-title-icon" aria-hidden="true">
                  {titleIcon}
                </span>
              )}
              {title}
            </h3>
            {titleAction}
          </div>
          {children}
        </div>
        {confirmDiscard && (
          <div
            className="dialog-backdrop"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setConfirmDiscard(false);
            }}
          >
            <div ref={confirmCardRef} className="dialog-card" role="alertdialog" aria-modal="true">
              <h3 className="dialog-title">{t("common.discardChangesTitle")}</h3>
              <p>{t("common.discardChangesMessage")}</p>
              <DialogActions
                primary={{
                  label: t("common.discardChangesConfirm"),
                  // Fade the outer dialog on discard, same as any other
                  // user-initiated close — this button IS closing the outer
                  // dialog, so it goes through `closeWithExit` rather than
                  // the raw `onClose` prop.
                  onClick: closeWithExit,
                  danger: true,
                }}
                onClose={() => setConfirmDiscard(false)}
                closeLabel={t("common.keepEditing")}
              />
            </div>
          </div>
        )}
      </div>
    </DialogCloseContext.Provider>,
    document.body
  );
}

/** Read the outer Dialog's own fading-close callback, for `DialogActions.tsx`'s Cancel button and anything else that would otherwise trigger a hard unmount by calling the parent's `onClose` prop directly. Returns `null` when used outside a `<Dialog>` (e.g. a standalone `<DialogActions>` embedded in some other surface). */
export function useDialogClose(): (() => void) | null {
  return useContext(DialogCloseContext);
}
